import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import type { SmartInsightsPropertyTagsUser } from 'lumen/models';

export type DropdownSmartInsightsRegionalVpFilterOption = Option & {
  id: string;
};

interface MarketSmartInsightsDropdownsDropdownRegionalVpFilterSignature {
  Args: {
    regionalVps: SmartInsightsPropertyTagsUser[];
  };
}

export default class MarketSmartInsightsDropdownsDropdownRegionalVpFilter extends Component<MarketSmartInsightsDropdownsDropdownRegionalVpFilterSignature> {
  @service declare intl: Services['intl'];
  @service declare portfolioSmartInsights: Services['portfolio-smart-insights'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): DropdownSmartInsightsRegionalVpFilterOption[] {
    return this.args.regionalVps
      .filter((regionalVp) => regionalVp.id !== null)
      .map((regionalVp) => ({
        label: regionalVp.name,
        value: regionalVp.id,
        id: regionalVp.id!,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRegionalVps() {
    const { selectedRegionalVpIds } = this.portfolioSmartInsights;

    return this.options.filter((regionalVpOption) =>
      selectedRegionalVpIds.includes(regionalVpOption.id),
    );
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildLabelWithCount(count: number) {
    if (count === 0) {
      return this.intl.t('market.smart_insights.filter.regional_vps');
    }

    if (count === this.options.length) {
      return this.intl.t(
        'market.smart_insights.filter.regional_vps.all_selected',
      );
    }

    return this.intl.t('market.smart_insights.filter.regional_vps.count', {
      count,
    });
  }

  @action onChange(
    options: DropdownSmartInsightsRegionalVpFilterOption[],
  ): void {
    const selectedRegionalVpIds = options.map((option) => option.id);

    this.portfolioSmartInsights.setSelectedRegionalVpIds(selectedRegionalVpIds);
  }

  <template>
    <PrismComboSelectInput
      @buildLabelWithCount={{this.buildLabelWithCount}}
      @canUnselectAll={{true}}
      @isSearchable={{true}}
      @onChange={{this.onChange}}
      @options={{this.options}}
      @selectAllLabel={{t
        "market.smart_insights.filter.regional_vps.all_selected"
      }}
      @value={{this.selectedRegionalVps}}
    />
  </template>
}
